import * as React from 'react';
import { FixMeLater } from '../../utils';
import { TemperatureChartMonth, TemperatureChartMonthProps } from './TemperatureChartMonth';
import { TemperatureChartContext, TemperatureChartContextValues } from './TemperatureChart.context';
import {
    activeBarHeightBuffer,
    BAR_LABEL_FONT_VARIANT,
    barLabelClearance,
    barSpacing,
    barWidth,
    pixelsPerDegree,
    topBuffer,
} from './TemperatureChart.constants';
import { useId } from '../../hooks';
import { TypographyBody, TypographyHeading } from '../Typography';
import { Stacker } from '../Stacker';
import { Styler } from '../Styler';
import { Summary, Temperatures } from './TemperatureChart.types';
import { STATIC_STYLES, useDynamicStyles } from './TemperatureChart.styles';
import { useCulture } from '../../culture';

export type TemperatureChartProps = {
    /**
     * The index of the active `TemperatureChartMonth` child component.
     */
    activeMonthIndex?: number;

    /**
     * The text used as an accessible description for the chart.
     */
    description: string;

    /**
     * The child `TemperatureChartMonth` components.
     */
    children: React.ReactElement<TemperatureChartMonthProps>[];

    /**
     * Callback fired when the value of the active month index is changed.
     */
    onChange?: (event: React.SyntheticEvent, index: number) => void;

    /**
     * The text used as an accessible title for the chart.
     */
    title: string;

    /**
     * The Labels that will be used in this component.
     * @default { min: 'Min', max: 'Max', average: 'Average' }
     */
    temperatureLabels?: { min: string; max: string; average: string };
};

export const TemperatureChart: React.ForwardRefExoticComponent<
    TemperatureChartProps & React.RefAttributes<HTMLElement>
> = React.forwardRef(function TemperatureChart(
    props: TemperatureChartProps,
    ref: React.ForwardedRef<FixMeLater>
): React.JSX.Element {
    const {
        activeMonthIndex,
        children,
        description,
        onChange,
        title,
        temperatureLabels = { min: 'Min', max: 'Max', average: 'Average' },
        ...other
    } = props;

    const { dir } = useCulture();
    const isRTL = dir === 'rtl';

    const numberOfMonths = React.Children.count(children);

    const months: React.ReactElement<TemperatureChartMonthProps>[] = [];
    let minAvg: number;
    let maxAvg = 0;
    let activeTemperatures: Temperatures = { min: 0, max: 0, avg: 0 };

    React.Children.forEach(children, (element, index) => {
        if (element && element.type === TemperatureChartMonth) {
            const { temperatures } = element.props;
            const { avg } = temperatures;
            const active = index === activeMonthIndex;
            if (minAvg === undefined || avg < minAvg) {
                minAvg = avg;
            }
            if (maxAvg === undefined || avg > maxAvg) {
                maxAvg = avg;
            }
            if (active) {
                activeTemperatures = temperatures;
            }
            months.push(
                React.cloneElement(element, {
                    index,
                    active,
                    onMouseEnter: onChange,
                })
            );
        }
    });

    // Reverse months array if RTL
    const displayedMonths = isRTL ? [...months].reverse() : months;

    const barBottom = maxAvg * pixelsPerDegree + topBuffer;
    const svgWidth = barWidth * numberOfMonths + barSpacing * (numberOfMonths - 1);
    const svgHeight = barBottom + activeBarHeightBuffer;

    const context: TemperatureChartContextValues = React.useMemo(() => {
        return {
            barBottom,
            nameY: barBottom + barLabelClearance,
            svgWidth,
        };
    }, [barBottom, svgWidth]);

    const id = useId();
    const titleId = `${id}-title`;
    const descriptionId = `${id}-description`;

    const dynamicStyles = useDynamicStyles(svgWidth, BAR_LABEL_FONT_VARIANT);

    const Averages: Record<string, Summary> = {
        min: {
            value: activeTemperatures.min,
            label: temperatureLabels.min,
        },
        average: {
            value: activeTemperatures.avg,
            label: temperatureLabels.average,
        },
        max: {
            value: activeTemperatures.max,
            label: temperatureLabels.max,
        },
    };

    return (
        <TemperatureChartContext.Provider value={context}>
            <svg
                ref={ref}
                css={STATIC_STYLES}
                style={dynamicStyles}
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                aria-labelledby={`${titleId} ${descriptionId}`}
                data-temperature-chart={true}
                {...other}
            >
                <title id={titleId}>{title}</title>
                <desc id={descriptionId}>{description}</desc>
                <g role="list">{displayedMonths}</g>
            </svg>
            <Styler mt={3}>
                <Stacker direction="row" spacing={6} justifyContent="center">
                    {Object.entries(Averages).map(([key, average]) => {
                        return (
                            <React.Fragment key={key}>
                                <Stacker alignItems="center" spacing={0.5}>
                                    <TypographyBody component="span" size="xs" color="text.light">
                                        {average.label}
                                    </TypographyBody>
                                    <TypographyHeading
                                        component="span"
                                        size="m"
                                        lineHeight={1}
                                        weight="bold"
                                        pos="relative"
                                    >
                                        {average.value}
                                        <Styler component="span" fontSize="0.5em" va="super">{`\u00b0C`}</Styler>
                                    </TypographyHeading>
                                </Stacker>
                            </React.Fragment>
                        );
                    })}
                </Stacker>
            </Styler>
        </TemperatureChartContext.Provider>
    );
});
